<template>
  <div class="mobile-admin-page">
    <div class="nav-bar-operations">
      <img
        src="@/assets/logogruas.png"
        alt="HomePage"
        class="logo-button"
        @click="backToModule"
      />
    </div>

    <div class="page-shell">
      <div class="card-strip">
        <div class="text-operations">
          <h2>Notificaciones push</h2>
        </div>
      </div>

      <div class="action-buttons">
        <button class="action-button" @click="backToModule">Volver al menú</button>
        <button
          @click="fetchMobilePushNotifications(1)"
          class="action-button"
          :disabled="mobilePushLoading"
        >
          {{ mobilePushLoading ? 'Actualizando…' : 'Actualizar historial' }}
        </button>
        <button
          @click="openMobilePushSendModal"
          class="action-button action-primary"
        >
          Enviar notificación
        </button>
      </div>

      <div class="table-container card-wrap">
        <h2>Historial de envíos</h2>
        <p style="margin: 0 0 12px; font-size: 13px; color: #666;">
          Historial de envíos a la app móvil (outbox). Las notificaciones descartadas
          o fallidas se pueden reintentar si siguen siendo relevantes.
        </p>

        <div
          style="display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; align-items: flex-end;"
        >
          <label style="font-size: 13px;">
            Estado
            <select
              v-model="mobilePushFilters.status"
              @change="fetchMobilePushNotifications(1)"
              style="display: block; margin-top: 4px; min-width: 140px;"
            >
              <option value="">Todos</option>
              <option
                v-for="opt in mobilePushStatusOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </label>
          <label style="font-size: 13px;">
            Tipo
            <select
              v-model="mobilePushFilters.notification_type"
              @change="fetchMobilePushNotifications(1)"
              style="display: block; margin-top: 4px; min-width: 180px;"
            >
              <option value="">Todos</option>
              <option
                v-for="opt in mobilePushTypeOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </label>
          <label style="font-size: 13px;">
            ID instalación
            <input
              v-model="mobilePushFilters.install_id"
              type="text"
              placeholder="UUID parcial"
              style="display: block; margin-top: 4px; min-width: 200px; padding: 4px 8px;"
              @keyup.enter="fetchMobilePushNotifications(1)"
            />
          </label>
          <label style="font-size: 13px;">
            Buscar
            <input
              v-model="mobilePushFilters.search"
              type="text"
              placeholder="Título, operador, unidad…"
              style="display: block; margin-top: 4px; min-width: 200px; padding: 4px 8px;"
              @keyup.enter="fetchMobilePushNotifications(1)"
            />
          </label>
          <button
            class="action-button"
            style="padding: 6px 12px; font-size: 12px;"
            @click="fetchMobilePushNotifications(1)"
          >
            Filtrar
          </button>
        </div>

        <p v-if="mobilePushError" class="text-danger" style="margin: 0 0 12px;">
          {{ mobilePushError }}
        </p>

        <table class="table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Tipo</th>
              <th>Título</th>
              <th>Destino</th>
              <th>Estado</th>
              <th>Detalle</th>
              <th style="min-width: 90px;">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!mobilePushLoading && mobilePushRows.length === 0">
              <td colspan="7" style="text-align: center; color: #888;">
                No hay notificaciones con los filtros actuales.
              </td>
            </tr>
            <tr v-for="row in mobilePushRows" :key="row.id">
              <td style="white-space: nowrap; font-size: 12px;">
                {{ formatMobileAppTimestamp(row.created_at) }}
              </td>
              <td style="font-size: 12px;">{{ row.notification_type_label }}</td>
              <td>
                <strong style="font-size: 13px;">{{ row.title }}</strong>
                <div style="font-size: 11px; color: #666; max-width: 280px;">
                  {{ row.body }}
                </div>
              </td>
              <td style="font-size: 12px;">
                <div v-if="row.unit_short_id"><strong>{{ row.unit_short_id }}</strong></div>
                <div v-if="row.last_operator_name">{{ row.last_operator_name }}</div>
                <code style="font-size: 10px;">{{ shortInstallId(row.install_id) }}</code>
              </td>
              <td>
                <span :class="mobilePushStatusClass(row.status)">
                  {{ row.status_label }}
                </span>
                <div v-if="row.sent_at" style="font-size: 10px; color: #666;">
                  Enviada: {{ formatMobileAppTimestamp(row.sent_at) }}
                </div>
              </td>
              <td style="font-size: 11px; color: #666; max-width: 200px;">
                <span v-if="row.drop_reason">Descartada: {{ row.drop_reason }}</span>
                <span v-else-if="row.last_error">{{ row.last_error }}</span>
                <span v-else-if="row.attempt_count > 0">Intentos: {{ row.attempt_count }}</span>
                <span v-else>—</span>
              </td>
              <td>
                <button
                  v-if="row.can_retry"
                  class="action-button"
                  style="padding: 4px 8px; font-size: 11px;"
                  :disabled="mobilePushRetryId === row.id"
                  @click="retryMobilePushNotification(row)"
                >
                  {{ mobilePushRetryId === row.id ? '…' : 'Reintentar' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="mobilePushTotalPages > 1"
          class="pagination-container"
          style="margin-top: 12px;"
        >
          <button
            @click="fetchMobilePushNotifications(mobilePushPage - 1)"
            :disabled="mobilePushPage <= 1 || mobilePushLoading"
          >
            <img src="@/assets/angulo-izquierdo.png" alt="Anterior" />
          </button>
          <span
            v-for="pageNumber in mobilePushTotalPages"
            :key="'push-page-' + pageNumber"
            @click="fetchMobilePushNotifications(pageNumber)"
            :class="{ active: pageNumber === mobilePushPage }"
          >
            {{ pageNumber }}
          </span>
          <button
            @click="fetchMobilePushNotifications(mobilePushPage + 1)"
            :disabled="mobilePushPage >= mobilePushTotalPages || mobilePushLoading"
          >
            <img src="@/assets/angulo-derecho.png" alt="Siguiente" />
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="mobilePushSendModalOpen"
      class="modal-overlay"
      @click.self="closeMobilePushSendModal"
    >
      <div class="modal-card" style="max-width: 520px; width: 95%;">
        <h3>Enviar notificación personalizada</h3>
        <p style="font-size: 13px; color: #666;">
          Envía a una instalación (UUID de Ajustes) y/o a todas las instalaciones
          del operador (ID de empleado en RRHH).
        </p>
        <div style="display: flex; flex-direction: column; gap: 10px; margin-top: 12px;">
          <label style="font-size: 13px;">
            Instalación (opcional)
            <select
              v-model="mobilePushSendForm.install_id"
              style="display: block; margin-top: 4px; width: 100%; padding: 6px 8px;"
            >
              <option value="">— Seleccionar instalación —</option>
              <option
                v-for="install in mobileAppInstalls"
                :key="install.install_id"
                :value="install.install_id"
              >
                {{ install.unit_short_id || 'Sin unidad' }} ·
                {{ install.last_operator_name || 'Sin operador' }} ·
                {{ shortInstallId(install.install_id) }}
              </option>
            </select>
          </label>
          <label style="font-size: 13px;">
            O pegar ID instalación
            <input
              v-model="mobilePushSendForm.install_id_manual"
              type="text"
              placeholder="UUID completo desde Ajustes"
              style="width: 100%; padding: 6px 8px; margin-top: 4px;"
            />
          </label>
          <label style="font-size: 13px;">
            ID empleado (opcional)
            <input
              v-model="mobilePushSendForm.employee_id"
              type="number"
              placeholder="PK del operador en RRHH"
              style="width: 100%; padding: 6px 8px; margin-top: 4px;"
            />
          </label>
          <label style="font-size: 13px;">
            Título
            <input
              v-model="mobilePushSendForm.title"
              type="text"
              maxlength="255"
              style="width: 100%; padding: 6px 8px; margin-top: 4px;"
            />
          </label>
          <label style="font-size: 13px;">
            Mensaje
            <textarea
              v-model="mobilePushSendForm.body"
              rows="3"
              style="width: 100%; padding: 6px 8px; margin-top: 4px;"
            />
          </label>
        </div>
        <div style="display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px;">
          <button class="action-button" @click="closeMobilePushSendModal">Cancelar</button>
          <button
            class="action-button action-primary"
            :disabled="mobilePushSendLoading"
            @click="submitMobilePushSend"
          >
            {{ mobilePushSendLoading ? 'Enviando…' : 'Enviar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  name: 'MobilePushAdmin',
  data() {
    return {
      permissions: [],
      isSuperuser: false,
      mobileAppInstalls: [],
      mobilePushRows: [],
      mobilePushLoading: false,
      mobilePushError: '',
      mobilePushPage: 1,
      mobilePushPageSize: 25,
      mobilePushTotalCount: 0,
      mobilePushFilters: {
        status: '',
        notification_type: '',
        install_id: '',
        search: '',
      },
      mobilePushStatusOptions: [],
      mobilePushTypeOptions: [],
      mobilePushRetryId: null,
      mobilePushSendModalOpen: false,
      mobilePushSendLoading: false,
      mobilePushSendForm: {
        install_id: '',
        install_id_manual: '',
        employee_id: '',
        title: '',
        body: '',
      },
    };
  },
  computed: {
    canManageMobileApp() {
      return this.hasPerm('access_control.manage_mobile_app') || this.hasPerm('manage_mobile_app');
    },
    mobilePushTotalPages() {
      return Math.max(1, Math.ceil(this.mobilePushTotalCount / this.mobilePushPageSize));
    },
  },
  created() {
    this.loadPermissions();
    this.fetchCurrentUser();
    this.fetchMobilePushMetadata();
    this.fetchMobilePushNotifications(1);
    // The install list feeds the destination picker of the send modal.
    this.fetchMobileAppInstalls();
  },
  methods: {
    loadPermissions() {
      try {
        const stored = localStorage.getItem('userPermissions');
        this.permissions = stored ? JSON.parse(stored) : [];
      } catch (error) {
        this.permissions = [];
      }
    },
    hasPerm(perm) {
      if (this.isSuperuser) return true;
      if (!this.permissions || this.permissions.length === 0) return false;
      return this.permissions.includes(perm) || this.permissions.includes(`access_control.${perm}`);
    },
    async fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY;
      try {
        const { data } = await axios.get(`${apiDirectory}/current-user/`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        });
        this.permissions = data.permissions || this.permissions;
        this.isSuperuser = !!data.is_superuser;
        if (!this.canManageMobileApp) {
          this.$router.push({ name: 'MainPanel' });
        }
      } catch (error) {
        this.$router.push({ name: 'MainPanel' });
      }
    },
    backToModule() {
      this.$router.push({ path: '/modulo-movil' });
    },
    formatMobileAppTimestamp(value) {
      if (!value) return '—';
      try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleString('es-MX');
      } catch (error) {
        return value;
      }
    },
    shortInstallId(value) {
      if (!value || typeof value !== 'string') return '—';
      if (value.length <= 14) return value;
      return `${value.slice(0, 8)}…${value.slice(-4)}`;
    },
    mobilePushStatusClass(status) {
      if (status === 'sent') return 'push-status push-status--sent';
      if (status === 'pending') return 'push-status push-status--pending';
      if (status === 'failed') return 'push-status push-status--failed';
      if (status === 'dropped') return 'push-status push-status--dropped';
      return 'push-status';
    },
    async fetchMobileAppInstalls() {
      const apiDirectory = process.env.API_DIRECTORY;
      try {
        const response = await axios.get(
          `${apiDirectory}/mobile-app-installs/`,
          { headers: { Authorization: 'Token ' + localStorage.getItem('authToken') } }
        );
        this.mobileAppInstalls = Array.isArray(response.data?.results) ? response.data.results : [];
      } catch (error) {
        console.error('Error fetching mobile app installs:', error);
      }
    },
    async fetchMobilePushMetadata() {
      const apiDirectory = process.env.API_DIRECTORY;
      try {
        const response = await axios.get(
          `${apiDirectory}/mobile-app-installs/pushes/metadata/`,
          { headers: { Authorization: 'Token ' + localStorage.getItem('authToken') } }
        );
        this.mobilePushStatusOptions = Array.isArray(response.data?.statuses)
          ? response.data.statuses
          : [];
        this.mobilePushTypeOptions = Array.isArray(response.data?.notification_types)
          ? response.data.notification_types
          : [];
      } catch (error) {
        console.error('Error fetching mobile push metadata:', error);
      }
    },
    async fetchMobilePushNotifications(page = 1) {
      const apiDirectory = process.env.API_DIRECTORY;
      this.mobilePushLoading = true;
      this.mobilePushError = '';
      this.mobilePushPage = page;
      try {
        const params = new URLSearchParams({
          page: String(page),
          page_size: String(this.mobilePushPageSize),
        });
        if (this.mobilePushFilters.status) {
          params.set('status', this.mobilePushFilters.status);
        }
        if (this.mobilePushFilters.notification_type) {
          params.set('notification_type', this.mobilePushFilters.notification_type);
        }
        if (this.mobilePushFilters.install_id.trim()) {
          params.set('install_id', this.mobilePushFilters.install_id.trim());
        }
        if (this.mobilePushFilters.search.trim()) {
          params.set('search', this.mobilePushFilters.search.trim());
        }
        const response = await axios.get(
          `${apiDirectory}/mobile-app-installs/pushes/?${params.toString()}`,
          { headers: { Authorization: 'Token ' + localStorage.getItem('authToken') } }
        );
        this.mobilePushRows = Array.isArray(response.data?.results) ? response.data.results : [];
        this.mobilePushTotalCount = Number(response.data?.count) || 0;
      } catch (error) {
        console.error('Error fetching mobile push notifications:', error);
        this.mobilePushError =
          error?.response?.data?.detail
          || error?.response?.data?.error
          || 'No se pudo cargar el historial de notificaciones.';
      } finally {
        this.mobilePushLoading = false;
      }
    },
    openMobilePushSendModal() {
      this.mobilePushSendForm = {
        install_id: '',
        install_id_manual: '',
        employee_id: '',
        title: '',
        body: '',
      };
      this.mobilePushSendModalOpen = true;
    },
    closeMobilePushSendModal() {
      if (this.mobilePushSendLoading) return;
      this.mobilePushSendModalOpen = false;
    },
    async submitMobilePushSend() {
      const title = (this.mobilePushSendForm.title || '').trim();
      const body = (this.mobilePushSendForm.body || '').trim();
      const installId = (
        this.mobilePushSendForm.install_id_manual
        || this.mobilePushSendForm.install_id
        || ''
      ).trim();
      const employeeRaw = (this.mobilePushSendForm.employee_id || '').toString().trim();
      const employeeId = employeeRaw ? Number(employeeRaw) : null;

      if (!title || !body) {
        Swal.fire({
          title: 'Campos requeridos',
          text: 'Título y mensaje son obligatorios.',
          icon: 'warning',
          confirmButtonText: 'Ok',
        });
        return;
      }
      if (!installId && (!employeeId || Number.isNaN(employeeId) || employeeId <= 0)) {
        Swal.fire({
          title: 'Destino requerido',
          text: 'Selecciona una instalación, pega su UUID, o indica un ID de empleado válido.',
          icon: 'warning',
          confirmButtonText: 'Ok',
        });
        return;
      }

      const apiDirectory = process.env.API_DIRECTORY;
      this.mobilePushSendLoading = true;
      let sendSucceeded = false;
      try {
        const payload = { title, body };
        if (installId) payload.install_id = installId;
        if (employeeId && employeeId > 0) payload.employee_id = employeeId;

        await axios.post(
          `${apiDirectory}/mobile-app-installs/pushes/send/`,
          payload,
          { headers: { Authorization: 'Token ' + localStorage.getItem('authToken') } }
        );
        sendSucceeded = true;
      } catch (error) {
        const data = error?.response?.data || {};
        Swal.fire({
          title: 'No se pudo enviar',
          text: data.detail || data.error || 'Error al encolar la notificación.',
          icon: 'error',
          confirmButtonText: 'Ok',
        });
      } finally {
        this.mobilePushSendLoading = false;
      }

      if (!sendSucceeded) return;

      this.mobilePushSendModalOpen = false;
      try {
        await this.fetchMobilePushNotifications(1);
      } catch (error) {
        console.error('Push list refresh failed after send:', error);
      }
      Swal.fire({
        title: 'Notificación encolada',
        text: 'La notificación se encoló correctamente. El historial se actualizará en unos segundos.',
        icon: 'success',
        timer: 2200,
        showConfirmButton: false,
      });
    },
    async retryMobilePushNotification(row) {
      if (!row?.id) return;
      const apiDirectory = process.env.API_DIRECTORY;
      this.mobilePushRetryId = row.id;
      try {
        await axios.post(
          `${apiDirectory}/mobile-app-installs/pushes/${row.id}/retry/`,
          {},
          { headers: { Authorization: 'Token ' + localStorage.getItem('authToken') } }
        );
        await this.fetchMobilePushNotifications(this.mobilePushPage);
        Swal.fire({
          title: 'Reintento procesado',
          text: 'Se volvió a intentar el envío.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        const data = error?.response?.data || {};
        Swal.fire({
          title: 'No se pudo reintentar',
          text: data.detail || data.error || 'Error al reintentar.',
          icon: 'error',
          confirmButtonText: 'Ok',
        });
      } finally {
        this.mobilePushRetryId = null;
      }
    },
  },
};
</script>

<style scoped src="@/styles/mobile-admin.css"></style>
